import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { Tooltip } from '../types';
import { apiService } from '../services/api';
import { useAuth } from './AuthContext';

interface TooltipContextType {
  tooltips: Tooltip[];
  loading: boolean;
  getTooltipById: (identificador: string) => Tooltip | undefined;
  refresh: () => Promise<void>;
}

const TooltipContext = createContext<TooltipContextType | undefined>(undefined);

export const TooltipProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [tooltips, setTooltips] = useState<Tooltip[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const { user } = useAuth();

  const fetchTooltips = useCallback(async () => {
    if (!user) {
      setTooltips([]);
      return;
    }
    
    try {
      setLoading(true);
      const data = await apiService.get<Tooltip[]>('/tooltips');
      setTooltips(data || []);
    } catch (error) {
      console.error('📋 [TooltipContext] Erro ao buscar tooltips:', error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchTooltips();
  }, [fetchTooltips]);

  const getTooltipById = useCallback((identificador: string) => {
    return tooltips.find(t => t.identificador === identificador && t.ativo);
  }, [tooltips]);

  return (
    <TooltipContext.Provider value={{ tooltips, loading, getTooltipById, refresh: fetchTooltips }}>
      {children}
    </TooltipContext.Provider>
  );
};

export const useTooltips = () => {
  const context = useContext(TooltipContext);
  if (!context) {
    throw new Error('useTooltips deve ser usado dentro de um TooltipProvider');
  }
  return context;
};
